/**
 * VÉRAULT TRAVELS - Client Intake Form
 * Secure, token-based intake form for gathering traveler details
 * Per spec: Universal form with adaptive sections (passport for international only)
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, CheckCircle, AlertCircle } from 'lucide-react';
import { IconSparkle, IconTravelers, IconPlane, IconHeart, VERAULT_COLORS } from '../components/VeraultIcons';
import { formatDateRange } from '../utils/dateFormat';

const API = process.env.REACT_APP_BACKEND_URL;

// Empty traveler form template
const EMPTY_TRAVELER = {
  legal_full_name: '',
  preferred_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  passport_number: '',
  passport_issuing_country: '',
  passport_expiration_date: '',
  dietary_needs: '',
  accessibility_notes: '',
  special_requirements: ''
};

function IntakeForm() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const [tripData, setTripData] = useState(null);
  const [travelers, setTravelers] = useState([{ ...EMPTY_TRAVELER }]);
  
  useEffect(() => {
    fetchIntakeData();
  }, [token]);
  
  const fetchIntakeData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API}/travels/intake/${token}`);
      setTripData(res.data);
      
      // Pre-populate with existing travelers if any
      if (res.data.existing_travelers?.length > 0) {
        setTravelers(res.data.existing_travelers.map(t => ({
          ...EMPTY_TRAVELER,
          ...t
        })));
      } else if (res.data.num_travelers > 1) {
        // Create empty forms for expected number of travelers
        const emptyTravelers = [];
        for (let i = 0; i < res.data.num_travelers; i++) {
          emptyTravelers.push({ ...EMPTY_TRAVELER });
        }
        setTravelers(emptyTravelers);
      }
    } catch (err) {
      console.error('Error fetching intake data:', err);
      if (err.response?.status === 404) {
        setError('This intake link is invalid or has expired.');
      } else if (err.response?.status === 400) {
        setError('Intake has already been completed for this trip.');
        setSubmitted(true);
      } else {
        setError('Unable to load intake form. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const updateTraveler = (index, field, value) => {
    setTravelers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };
  
  const addTraveler = () => {
    setTravelers(prev => [...prev, { ...EMPTY_TRAVELER }]);
  };
  
  const removeTraveler = (index) => {
    if (travelers.length <= 1) return;
    setTravelers(prev => prev.filter((_, i) => i !== index));
  };
  
  const validateForm = () => {
    for (let i = 0; i < travelers.length; i++) {
      const t = travelers[i];
      if (!t.legal_full_name?.trim()) {
        toast.error(`Please enter legal full name for Traveler ${i + 1}`);
        return false;
      }
      if (!t.date_of_birth) {
        toast.error(`Please enter date of birth for Traveler ${i + 1}`);
        return false;
      }
      if (!t.email?.trim() && !t.phone?.trim()) {
        toast.error(`Please enter email or phone for Traveler ${i + 1}`);
        return false;
      }
      
      // International trips require passport info
      if (tripData?.requires_passport) {
        if (!t.passport_number?.trim()) {
          toast.error(`Please enter passport number for Traveler ${i + 1}`);
          return false;
        }
        if (!t.passport_issuing_country?.trim()) {
          toast.error(`Please enter passport country for Traveler ${i + 1}`);
          return false;
        }
        if (!t.passport_expiration_date) {
          toast.error(`Please enter passport expiration for Traveler ${i + 1}`);
          return false;
        }
      }
    }
    return true;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/travels/intake/${token}/submit`, {
        trip_id: tripData.trip_id,
        travelers: travelers.filter(t => t.legal_full_name?.trim())
      });
      
      setSubmitted(true);
      toast.success('Thank you! Your travel details have been submitted.');
    } catch (err) {
      console.error('Error submitting intake:', err);
      toast.error(err.response?.data?.detail || 'Failed to submit intake form');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F1EB] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#C8A85A] mx-auto mb-4" />
          <p className="text-[#8C8A86]">Loading your intake form...</p>
        </div>
      </div>
    );
  }
  
  if (error && !submitted) {
    return (
      <div className="min-h-screen bg-[#F5F1EB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-[rgba(0,0,0,0.06)]">
          <CardHeader className="text-center">
            <AlertCircle className="w-12 h-12 text-[#8C8A86] mx-auto mb-2" />
            <CardTitle className="font-serif text-xl text-[#1A1A1A]">Unable to Load Form</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <p className="text-sm text-[#8C8A86]">
              If you believe this is an error, please contact your travel advisor.
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }
  
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F5F1EB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-[rgba(0,0,0,0.06)]">
          <CardHeader className="text-center">
            <IconHeart size={48} color={VERAULT_COLORS.gold} className="mx-auto mb-2" />
            <CardTitle className="font-serif text-2xl text-[#1A1A1A]">Thank You</CardTitle>
            <CardDescription className="text-base">
              Your travel details have been received. Your advisor will begin designing your personalized itinerary.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <div className="bg-[#EEE9E2] rounded-lg p-4">
              <p className="text-sm text-[#8C8A86]">
                You will receive updates as your trip takes shape.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#F5F1EB]">
      {/* Header */}
      <header className="bg-white border-b border-[rgba(0,0,0,0.06)] py-6">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center">
            <h1 className="font-serif text-2xl text-[#1A1A1A] mb-1">VÉRAULT</h1>
            <p className="text-sm text-[#8C8A86] tracking-wider uppercase">Travel Details</p>
          </div>
        </div>
      </header>
      
      {/* Trip Info Banner */}
      <div className="bg-[#1A1A1A] text-white py-4">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <IconPlane size={16} color="#C8A85A" />
            <span className="font-serif text-lg">{tripData?.trip_title || tripData?.destinations}</span>
          </div>
          <p className="text-sm text-[#8C8A86]">
            {tripData?.start_date && tripData?.end_date && 
              formatDateRange(tripData.start_date, tripData.end_date)
            }
          </p>
        </div>
      </div>
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit}>
          {/* Introduction */}
          <Card className="mb-6 border-[rgba(0,0,0,0.06)]">
            <CardHeader>
              <CardTitle className="font-serif text-xl text-[#1A1A1A] flex items-center gap-2">
                <IconSparkle size={20} color={VERAULT_COLORS.gold} />
                Welcome to Your Journey
              </CardTitle>
              <CardDescription>
                Please provide the following details for each traveler. This information helps us create a seamless experience and ensures all bookings are accurate.
              </CardDescription>
            </CardHeader>
            {tripData?.requires_passport && (
              <CardContent>
                <div className="bg-[#C8A85A]/10 border border-[#C8A85A]/20 rounded-lg p-4">
                  <p className="text-sm text-[#1A1A1A]">
                    <strong>International Travel Notice:</strong> This trip requires valid passport information for all travelers. Please ensure passport details are accurate and passports will not expire within 6 months of your travel dates.
                  </p>
                </div>
              </CardContent>
            )}
          </Card>
          
          {/* Traveler Forms */}
          {travelers.map((traveler, index) => (
            <Card key={index} className="mb-6 border-[rgba(0,0,0,0.06)]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-lg text-[#1A1A1A] flex items-center gap-2">
                    <IconTravelers size={20} color={VERAULT_COLORS.stone} />
                    Traveler {index + 1}
                  </CardTitle>
                  {travelers.length > 1 && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removeTraveler(index)}
                      className="text-[#8C8A86] hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Identity Section */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-[#8C8A86] uppercase tracking-wider">Identity</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`legal_name_${index}`}>Legal Full Name *</Label>
                      <Input
                        id={`legal_name_${index}`}
                        value={traveler.legal_full_name}
                        onChange={(e) => updateTraveler(index, 'legal_full_name', e.target.value)}
                        placeholder="As shown on passport"
                        className="bg-white"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`preferred_name_${index}`}>Preferred Name</Label>
                      <Input
                        id={`preferred_name_${index}`}
                        value={traveler.preferred_name}
                        onChange={(e) => updateTraveler(index, 'preferred_name', e.target.value)}
                        placeholder="What you like to be called"
                        className="bg-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`dob_${index}`}>Date of Birth *</Label>
                      <Input
                        id={`dob_${index}`}
                        type="date"
                        value={traveler.date_of_birth}
                        onChange={(e) => updateTraveler(index, 'date_of_birth', e.target.value)}
                        className="bg-white"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                {/* Contact Section */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-[#8C8A86] uppercase tracking-wider">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`email_${index}`}>Email *</Label>
                      <Input
                        id={`email_${index}`}
                        type="email"
                        value={traveler.email}
                        onChange={(e) => updateTraveler(index, 'email', e.target.value)}
                        placeholder="email@example.com"
                        className="bg-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`phone_${index}`}>Phone</Label>
                      <Input
                        id={`phone_${index}`}
                        type="tel"
                        value={traveler.phone}
                        onChange={(e) => updateTraveler(index, 'phone', e.target.value)}
                        placeholder="+1 (555) 000-0000"
                        className="bg-white"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Passport Section - Only for International Trips */}
                {tripData?.requires_passport && (
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-[#8C8A86] uppercase tracking-wider">Passport Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor={`passport_number_${index}`}>Passport Number *</Label>
                        <Input
                          id={`passport_number_${index}`}
                          value={traveler.passport_number}
                          onChange={(e) => updateTraveler(index, 'passport_number', e.target.value)}
                          placeholder="Passport number"
                          className="bg-white"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`passport_country_${index}`}>Issuing Country *</Label>
                        <Input
                          id={`passport_country_${index}`}
                          value={traveler.passport_issuing_country}
                          onChange={(e) => updateTraveler(index, 'passport_issuing_country', e.target.value)}
                          placeholder="e.g., United States"
                          className="bg-white"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor={`passport_expiry_${index}`}>Expiration Date *</Label>
                        <Input
                          id={`passport_expiry_${index}`}
                          type="date"
                          value={traveler.passport_expiration_date}
                          onChange={(e) => updateTraveler(index, 'passport_expiration_date', e.target.value)}
                          className="bg-white"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Special Requirements Section */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-[#8C8A86] uppercase tracking-wider">Special Requirements</h4>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor={`dietary_${index}`}>Dietary Needs</Label>
                      <Input
                        id={`dietary_${index}`}
                        value={traveler.dietary_needs}
                        onChange={(e) => updateTraveler(index, 'dietary_needs', e.target.value)}
                        placeholder="Vegetarian, allergies, etc."
                        className="bg-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`accessibility_${index}`}>Accessibility Notes</Label>
                      <Textarea
                        id={`accessibility_${index}`}
                        value={traveler.accessibility_notes}
                        onChange={(e) => updateTraveler(index, 'accessibility_notes', e.target.value)}
                        placeholder="Mobility requirements, special assistance needed, etc."
                        className="bg-white"
                        rows={2}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`special_${index}`}>Other Requirements</Label>
                      <Textarea
                        id={`special_${index}`}
                        value={traveler.special_requirements}
                        onChange={(e) => updateTraveler(index, 'special_requirements', e.target.value)}
                        placeholder="Any other requirements or preferences"
                        className="bg-white"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {/* Add Another Traveler */}
          <Button
            type="button"
            variant="outline"
            onClick={addTraveler}
            className="w-full mb-6 border-dashed border-[#8C8A86] text-[#8C8A86] hover:bg-[#EEE9E2]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Another Traveler
          </Button>
          
          {/* Submit */}
          <Card className="border-[rgba(0,0,0,0.06)]">
            <CardContent className="pt-6">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1A1A1A] hover:bg-[#2C2C2C] text-white h-12"
                data-testid="intake-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit Travel Details
                  </>
                )}
              </Button>
              <p className="text-xs text-center text-[#8C8A86] mt-4">
                Your information is securely transmitted and stored. By submitting, you confirm that all details are accurate.
              </p>
            </CardContent>
          </Card>
        </form>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t border-[rgba(0,0,0,0.06)] py-6 mt-8">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-xs text-[#8C8A86]">
            VÉRAULT TRAVELS · Questions? Contact your travel advisor directly.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default IntakeForm;
